import React, {Component} from "react";
import {Button, CardBody, Dropdown, GdAutocomplete, GdCheckbox, GdRadio, Input, StateOptions} from "gd-react";
import {format as formatDate, isValid, parse as parseDate} from "date-fns";
import GdModal from "../../components/GdModal/GdModal";
import GridDuck from "gridduck";
import {detectCsvColumns} from "../../services/csvColumnDetect";
import "./dataFileImport.scss";

const MAX_CSV_BYTES = 256 * 1024;

const VALUE_TYPE_ITEMS = [
    {id: "string", title: "String", value: "string"},
    {id: "number", title: "Number", value: "number"}
];

// Sentinel value for the "None" option (the Dropdown can't use "" — it reads as "nothing selected").
const NONE_VALUE = "__none__";

// CSV layout options, shown as radio cards with a small sample table so the format is obvious
// (reusing the item_name_column / device_per_column pattern from CreateDataUpload):
// "standard" maps each value column to a data type; "deviceHeader" treats every column header
// (after the start column) as a device code, all sharing one data type.
const LAYOUT_OPTIONS = [
    {
        name: "standard",
        value: "standard",
        label: (
            <div className={"column layout-option"}>
                <label>A column per data type</label>
                <table border={"1"} className={"rows"}>
                    <tbody>
                    <tr><td><strong>Date</strong></td><td><strong>Device</strong></td><td><strong>Power</strong></td><td><strong>Energy</strong></td></tr>
                    <tr><td>08:00</td><td>A1</td><td>100</td><td>50</td></tr>
                    <tr><td>09:00</td><td>A1</td><td>120</td><td>60</td></tr>
                    </tbody>
                </table>
            </div>
        )
    },
    {
        name: "deviceHeader",
        value: "deviceHeader",
        label: (
            <div className={"column layout-option"}>
                <label>A column per device</label>
                <table border={"1"} className={"columns"}>
                    <tbody>
                    <tr><td><strong>Date</strong></td><td><strong>Device 1</strong></td><td><strong>Device 2</strong></td><td><strong>Device 3</strong></td></tr>
                    <tr><td>08:00</td><td>100</td><td>84</td><td>140</td></tr>
                    <tr><td>09:00</td><td>212</td><td>513</td><td>214</td></tr>
                    </tbody>
                </table>
            </div>
        )
    }
];

// Data-type options for value columns, mirroring the alert editor: icon + "Category Label (unit)".
// The value is the DataTypeTypes enum (what the import format/backend expects), sourced from the
// static state-options catalog rather than the org's data types.
function buildDataTypeOptions() {
    const options = [];
    StateOptions.forEach((stateOption) => {
        (stateOption.dataTypes || []).forEach((dataType) => {
            if (options.find((o) => o.value === dataType.type)) return;
            options.push({
                id: dataType.type,
                value: dataType.type,
                title: (dataType.category !== dataType.label ? dataType.category + " " : "") + dataType.label + " (" + dataType.unit + ")",
                category: dataType.category,
                icon: dataType.categoryDetails && dataType.categoryDetails.icon,
                color: dataType.categoryDetails && dataType.categoryDetails.color
            });
        });
    });
    return options.sort((a, b) => (a.category || "").localeCompare(b.category || ""));
}

// Per-type processing (multiplier / accumulating) is keyed by data type in format.dataTypes.
// Build that map from {type, multiplier, isAccumulated} entries, skipping empties. If two entries
// share a type the populated values merge — types are normally unique across an import.
function buildDataTypes(entries) {
    let out = {};
    entries.forEach((e) => {
        if (!e.type) return;
        let cfg = {};
        if (e.multiplier !== "" && e.multiplier != null && !isNaN(Number(e.multiplier))) cfg.multiplier = Number(e.multiplier);
        if (e.isAccumulated) cfg.isAccumulated = true;
        if (Object.keys(cfg).length) out[e.type] = Object.assign({}, out[e.type], cfg);
    });
    return out;
}

// Modal to set up a data import config (dataFileImport): details, site/device and CSV format mapping.
class CreateDataFileImport extends Component {
    constructor(props) {
        super(props);
        const item = props.item;
        const fmt = (item && item.format) || {};
        const dc = (item && item.device_config) || {};
        // Per-type processing (multiplier / accumulating) now lives in fmt.dataTypes, keyed by data type.
        const dataTypesCfg = fmt.dataTypes || {};
        const fmtValues = fmt.values && fmt.values.length
            ? fmt.values.map((v) => ({
                index: v.index !== undefined ? String(v.index) : "",
                type: v.type || "",
                multiplier: dataTypesCfg[v.type] && dataTypesCfg[v.type].multiplier != null ? String(dataTypesCfg[v.type].multiplier) : "",
                isAccumulated: !!(dataTypesCfg[v.type] && dataTypesCfg[v.type].isAccumulated)
            }))
            : [{index: "", type: "", multiplier: "", isAccumulated: false}];
        const headerTypeCfg = (fmt.dataType && dataTypesCfg[fmt.dataType]) || {};
        this.state = {
            name: (item && item.name) || "",
            description: (item && item.description) || "",
            site: item && item.site_id ? {id: item.site_id, title: ""} : null,
            asset: item && item.asset_id ? {id: item.asset_id, title: ""} : null,
            autoCreateAsset: item ? !!item.auto_create_asset : false,
            formatName: fmt.name || "",
            formatDescription: fmt.description || "",
            deviceCodeIndex: fmt.deviceCode && fmt.deviceCode.index !== undefined ? String(fmt.deviceCode.index) : "",
            deviceCodeValueType: (fmt.deviceCode && fmt.deviceCode.valueType) || "string",
            dateIndex: fmt.date && fmt.date.index !== undefined ? String(fmt.date.index) : "",
            dateFormat: (fmt.date && fmt.date.format) || "",
            values: fmtValues,
            deviceFromHeader: !!fmt.deviceFromHeader,
            headerDataType: fmt.dataType || "",
            deviceStartIndex: fmt.deviceStartIndex !== undefined ? String(fmt.deviceStartIndex) : "",
            headerMultiplier: headerTypeCfg.multiplier != null ? String(headerTypeCfg.multiplier) : "",
            headerIsAccumulated: !!headerTypeCfg.isAccumulated,
            thirdPartyType: dc.thirdPartyType || "",
            zigbeeProductName: dc.zigbeeProductName || "",
            zigbeeManufacturerName: dc.zigbeeManufacturerName || "",
            dataOnly: !!dc.dataOnly,
            dataTypes: buildDataTypeOptions(),
            csvFileName: "",
            csvText: "",
            columns: [],
            hasHeader: true,
            csvError: null,
            triedToSave: false,
            page: 1
        };
        this.errors = {};
        [
            "getSites", "getAssets", "updateField",
            "addValueColumn", "removeValueColumn", "updateValueColumn",
            "onCsvSelected", "toggleHasHeader", "renderColumnSelect", "datePreview",
            "checkErrors", "save", "closeModal", "buildPayload"
        ].forEach((f) => (this[f] = this[f].bind(this)));
    }

    async componentDidMount() {
        const item = this.props.item;
        if (item && item.site_id) {
            try {
                let s = await GridDuck.getSite({id: item.site_id});
                this.setState({site: {id: s.id, title: s.name}});
            } catch (e) { /* leave id-only value */ }
        }
        if (item && item.asset_id) {
            try {
                let a = await GridDuck.getAsset({id: item.asset_id});
                this.setState({asset: {id: a.id, title: a.name}});
            } catch (e) { /* leave id-only value */ }
        }
    }

    async getSites(searchTerm, reset) {
        let self = this;
        if (reset) this.siteOffset = 0;
        let filters = [];
        let limit = 25;
        if (searchTerm) filters.push({field: "search", value: searchTerm});
        let res = await GridDuck.getSites({filters: filters, offset: this.siteOffset || 0, items: limit});
        self.siteOffset = (self.siteOffset || 0) + limit;
        return res.list
            .filter((s) => s._permission === "admin")
            .map((s) => ({id: s.id, title: s.name, subtitle: s.locationName || "", icon: "FaBuilding"}));
    }

    async getAssets(searchTerm, reset) {
        let self = this;
        if (reset) this.assetOffset = 0;
        let filters = [];
        let limit = 25;
        if (searchTerm) filters.push({field: "search", value: searchTerm});
        if (this.state.site) filters.push({field: "siteId", value: this.state.site.id});
        let res = await GridDuck.getAssets({filters: filters, offset: this.assetOffset || 0, items: limit});
        self.assetOffset = (self.assetOffset || 0) + limit;
        return res.list.map((a) => ({id: a.id, title: a.name, subtitle: a.siteName || "", icon: "FaMicrochip"}));
    }

    updateField(field, value) {
        this.setState({[field]: value});
    }

    addValueColumn() {
        this.setState({values: this.state.values.concat([{index: "", type: "", multiplier: "", isAccumulated: false}])});
    }

    removeValueColumn(i) {
        let values = this.state.values.slice();
        values.splice(i, 1);
        if (!values.length) values.push({index: "", type: "", multiplier: "", isAccumulated: false});
        this.setState({values: values});
    }

    updateValueColumn(i, field, value) {
        let values = this.state.values.slice();
        values[i] = Object.assign({}, values[i], {[field]: value});
        this.setState({values: values});
    }

    detectColumns(text, hasHeader) {
        try {
            return {columns: detectCsvColumns(text, hasHeader), csvError: null};
        } catch (e) {
            return {columns: [], csvError: "Couldn't parse this CSV file."};
        }
    }

    onCsvSelected(e) {
        let file = e.target.files && e.target.files[0];
        if (!file) return;
        let self = this;
        let reader = new FileReader();
        reader.onload = () => {
            let text = reader.result;
            self.setState(Object.assign({csvFileName: file.name, csvText: text}, self.detectColumns(text, self.state.hasHeader)));
        };
        reader.onerror = () => self.setState({csvError: "Couldn't read this file."});
        reader.readAsText(file.slice(0, MAX_CSV_BYTES));
    }

    toggleHasHeader(hasHeader) {
        this.setState(Object.assign({hasHeader: hasHeader},
            this.state.csvText ? this.detectColumns(this.state.csvText, hasHeader) : {}));
    }

    // A column picker: a Dropdown of detected CSV columns when a sample CSV is loaded, else free text.
    // allowEmpty adds a "None" option (for optional fields like the device code column).
    renderColumnSelect(label, value, onChange, errorKey, allowEmpty) {
        let tried = this.state.triedToSave;
        if (this.state.columns.length) {
            // Use string values so the first column (index 0) is selectable — the Dropdown treats
            // a falsy value (0) as "nothing selected" and shows the placeholder instead.
            let items = this.state.columns.map((c) => ({
                id: String(c.index),
                value: String(c.index),
                title: (
                    <span className={"column-option"}>
                        <span className={"column-option-name"}>{c.name}</span>
                        {c.samples.length ?
                            <span className={"column-option-samples"}>{c.samples.join(", ")}</span> : null}
                    </span>
                )
            }));
            if (allowEmpty) {
                items.unshift({id: NONE_VALUE, value: NONE_VALUE, title: "None"});
            }
            let empty = value === "" || value === null || value === undefined;
            return (
                <Dropdown
                    label={label}
                    placeholder={"Select a column"}
                    value={empty ? (allowEmpty ? NONE_VALUE : undefined) : String(value)}
                    fixeditems={items}
                    error={errorKey ? this.errors[errorKey] && tried : false}
                    onChange={(e) => onChange(e.target.value === NONE_VALUE ? "" : e.target.value)}
                />
            );
        }
        return (
            <Input
                required
                label={label}
                value={value}
                error={errorKey ? this.errors[errorKey] && tried : false}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    // Live feedback: parse the selected date column's sample with the entered date-fns format.
    datePreview() {
        if (this.state.dateIndex === "" || !this.state.dateFormat) return null;
        let col = this.state.columns.find((c) => String(c.index) === String(this.state.dateIndex));
        let sample = col && col.samples[0];
        if (!sample) return null;
        let parsed;
        let hint;
        try {
            parsed = parseDate(sample, this.state.dateFormat, new Date());
        } catch (e) {
            // date-fns throws on misused tokens (e.g. moment-style DD/YYYY); show its first sentence.
            hint = e && e.message ? e.message.split(";")[0] : null;
        }
        if (parsed && isValid(parsed)) {
            return <p className={"preview-ok"}>{"✓ \"" + sample + "\" → " + formatDate(parsed, "yyyy-MM-dd HH:mm:ss")}</p>;
        }
        return <p className={"error-text"}>{"✗ Couldn't parse \"" + sample + "\" with \"" + this.state.dateFormat + "\"" + (hint ? " — " + hint : "")}</p>;
    }

    // deviceCode is optional when a specific asset is targeted (asset_id will be sent); required otherwise.
    deviceCodeOptional() {
        return !this.state.autoCreateAsset && !!this.state.asset;
    }

    checkErrors() {
        let errors = {};
        if (!this.state.name) errors["name"] = true;
        if (!this.state.site) errors["siteId"] = true;
        if (!this.state.formatName) errors["formatName"] = true;
        if (this.state.dateIndex === "") errors["dateIndex"] = true;
        if (!this.state.dateFormat) errors["dateFormat"] = true;
        if (this.state.deviceFromHeader) {
            // Device-on-header: rows route by column header, so only the shared data type is required.
            if (!this.state.headerDataType) errors["headerDataType"] = true;
        } else {
            if (!this.deviceCodeOptional() && this.state.deviceCodeIndex === "") errors["deviceCodeIndex"] = true;
            if (!this.state.values.filter((v) => v.index !== "" && v.type).length) errors["values"] = true;
        }
        this.errors = errors;
    }

    // Index can be a column number or a header name, so keep strings but coerce numerics.
    buildPayload() {
        let s = this.state;
        let toIndex = (v) => (v !== "" && !isNaN(Number(v)) ? Number(v) : v);
        let format = {
            name: s.formatName,
            type: "csv",
            date: {index: toIndex(s.dateIndex), format: s.dateFormat}
        };
        if (s.deviceFromHeader) {
            // Device-on-header: every column header (from deviceStartIndex on) is a device code,
            // all sharing one data type. values/deviceCode/assetId don't apply in this mode.
            format.deviceFromHeader = true;
            format.dataType = s.headerDataType;
            if (s.deviceStartIndex !== "" && !isNaN(Number(s.deviceStartIndex))) {
                format.deviceStartIndex = Number(s.deviceStartIndex);
            }
            let dataTypes = buildDataTypes([{type: s.headerDataType, multiplier: s.headerMultiplier, isAccumulated: s.headerIsAccumulated}]);
            if (Object.keys(dataTypes).length) format.dataTypes = dataTypes;
        } else {
            let cols = s.values.filter((v) => v.index !== "" && v.type);
            format.values = cols.map((v) => ({index: toIndex(v.index), type: v.type}));
            let dataTypes = buildDataTypes(cols);
            if (Object.keys(dataTypes).length) format.dataTypes = dataTypes;
            if (s.deviceCodeIndex !== "") {
                format.deviceCode = {index: toIndex(s.deviceCodeIndex), valueType: s.deviceCodeValueType};
            }
        }
        if (s.formatDescription) format.description = s.formatDescription;
        let deviceConfig = {};
        if (s.thirdPartyType) deviceConfig.thirdPartyType = s.thirdPartyType;
        if (s.zigbeeProductName) deviceConfig.zigbeeProductName = s.zigbeeProductName;
        if (s.zigbeeManufacturerName) deviceConfig.zigbeeManufacturerName = s.zigbeeManufacturerName;
        if (s.dataOnly) deviceConfig.dataOnly = true;
        let payload = {
            name: s.name,
            site_id: s.site.id,
            auto_create_asset: s.autoCreateAsset,
            device_config: deviceConfig,
            format: format
        };
        if (s.description) payload.description = s.description;
        // assetId is ignored in device-on-header (rows route by header), so only send it for the standard layout.
        if (!s.deviceFromHeader && !s.autoCreateAsset && s.asset) payload.asset_id = s.asset.id;
        return payload;
    }

    save() {
        let self = this;
        this.setState({triedToSave: true});
        this.checkErrors();
        if (Object.values(this.errors).find((e) => e)) {
            this.forceUpdate();
            return Promise.reject(new Error("Missing required fields"));
        }
        const payload = this.buildPayload();
        const req = this.props.item ? this.props.item.set(payload) : GridDuck.createDataFileImport(payload);
        return req.then((res) => {
            if (self.props.onCreated) self.props.onCreated(res);
            self.closeModal(res);
            return res;
        });
    }

    closeModal(res) {
        this.props.onClose(res);
    }

    // Gate "Next" on the current step's required fields (final step uses Save + full checkErrors).
    blockNext() {
        if (this.state.page === 1) return !this.state.name;
        if (this.state.page === 2) return !this.state.site;
        return false;
    }

    stepWrapper(title, help, body) {
        return (
            <div className={"details"}>
                <div className={"details-inner"}>
                    <div className={"inner-header"}>
                        <p className={"title-row"}>{title}</p>
                        <p className={"help-row"}>{help}</p>
                    </div>
                    <div className={"inner-body"}>{body}</div>
                </div>
            </div>
        );
    }

    // Standard layout: a device-code column plus one row per value column (each mapped to a data type).
    renderStandardLayout(tried) {
        return (
            <div>
                <p className={"sub-heading"}>{"Device code column" + (this.deviceCodeOptional() ? " (optional)" : "")}</p>
                {this.deviceCodeOptional() ?
                    <p className={"info-text"}>A device is selected, so all rows import to it — a device code column isn't needed.</p> : null}
                <div className={"value-row"}>
                    {this.renderColumnSelect("Column", this.state.deviceCodeIndex,
                        (v) => this.updateField("deviceCodeIndex", v), this.deviceCodeOptional() ? null : "deviceCodeIndex", true)}
                    <Dropdown
                        label={"Value type"}
                        value={this.state.deviceCodeValueType}
                        fixeditems={VALUE_TYPE_ITEMS}
                        onChange={(e) => this.updateField("deviceCodeValueType", e.target.value)}
                    />
                </div>

                <p className={"sub-heading"}>Value columns</p>
                {this.errors["values"] && tried ? (
                    <p className={"error-text"}>Add at least one value column with a column and data type.</p>
                ) : null}
                {this.state.values.map((v, i) => (
                    <div className={"value-column"} key={i}>
                        <div className={"value-row"}>
                            {this.renderColumnSelect("Column", v.index,
                                (val) => this.updateValueColumn(i, "index", val), null)}
                            <Dropdown
                                label={"Data type"}
                                value={v.type}
                                fixeditems={this.state.dataTypes}
                                placeholder={"Select a data type"}
                                onChange={(e) => this.updateValueColumn(i, "type", e.target.value)}
                            />
                            <Input
                                label={"Multiplier"}
                                type={"number"}
                                placeholder={"1"}
                                value={v.multiplier}
                                onChange={(e) => this.updateValueColumn(i, "multiplier", e.target.value)}
                            />
                        </div>
                        <div className={"value-row value-row-options"}>
                            <GdCheckbox
                                label={"Accumulating value (store the running total)"}
                                checked={v.isAccumulated}
                                onChange={(e) => this.updateValueColumn(i, "isAccumulated", e.target.checked)}
                            />
                            <Button
                                label={"Remove"}
                                color={"gd-red"}
                                additionalclasses={"sm"}
                                onClick={() => this.removeValueColumn(i)}
                            />
                        </div>
                    </div>
                ))}
                <div className={"row"} style={{marginTop: "10px"}}>
                    <Button label={"+ Add value column"} additionalclasses={"md"} onClick={this.addValueColumn}/>
                </div>
            </div>
        );
    }

    // Device-on-header layout: every column header (from the start column on) is a device code;
    // all share one data type. Pick the type, where device columns start, and per-type processing.
    renderHeaderLayout(tried) {
        return (
            <div>
                <p className={"info-text"}>Use this layout when each device has its own column. Set up: pick the data type the columns hold, then choose the first device column — every column from there on is read as a separate device, using its header text as the device code.</p>

                <p className={"sub-heading"}>Data type</p>
                <div className={"value-row"}>
                    <Dropdown
                        label={"Data type"}
                        value={this.state.headerDataType}
                        fixeditems={this.state.dataTypes}
                        placeholder={"Select a data type"}
                        error={this.errors["headerDataType"] && tried}
                        onChange={(e) => this.updateField("headerDataType", e.target.value)}
                    />
                </div>

                <p className={"sub-heading"}>First device column (optional)</p>
                <p className={"info-text"}>Pick the column where the device readings start — every column from here on becomes a device. Columns before it (besides the date column) are ignored. Defaults to the first column.</p>
                <div className={"value-row"}>
                    {this.renderColumnSelect("First device column", this.state.deviceStartIndex,
                        (v) => this.updateField("deviceStartIndex", v), null, true)}
                </div>

                <p className={"sub-heading"}>Processing</p>
                <div className={"value-row"}>
                    <Input
                        label={"Multiplier"}
                        type={"number"}
                        placeholder={"1"}
                        value={this.state.headerMultiplier}
                        onChange={(e) => this.updateField("headerMultiplier", e.target.value)}
                    />
                </div>
                <div className={"value-row value-row-options"}>
                    <GdCheckbox
                        label={"Accumulating value (store the running total)"}
                        checked={this.state.headerIsAccumulated}
                        onChange={(e) => this.updateField("headerIsAccumulated", e.target.checked)}
                    />
                </div>
            </div>
        );
    }

    renderStep() {
        let tried = this.state.triedToSave;
        switch (this.state.page) {
            case 1:
                return this.stepWrapper("Details", "Name and describe this import.", (
                    <div>
                        <div className={"row"}>
                            <Input
                                required
                                top="true"
                                label={"Name"}
                                value={this.state.name}
                                error={this.errors["name"] && tried}
                                onChange={(e) => this.updateField("name", e.target.value)}
                            />
                        </div>
                        <div className={"row"} style={{marginTop: "15px"}}>
                            <Input
                                label={"Description"}
                                value={this.state.description}
                                onChange={(e) => this.updateField("description", e.target.value)}
                            />
                        </div>
                        <div className={"row with-body-item"} style={{marginTop: "15px"}}>
                            <GdRadio
                                row
                                name={"csvLayout"}
                                label={"CSV layout"}
                                value={this.state.deviceFromHeader ? "deviceHeader" : "standard"}
                                options={LAYOUT_OPTIONS}
                                onChange={(e) => this.updateField("deviceFromHeader", e.target.value === "deviceHeader")}
                            />
                        </div>
                    </div>
                ));
            case 2:
                return this.stepWrapper("Site & device", "Where should imported data go?", (
                    <div>
                        <div className={"row"}>
                            <GdAutocomplete
                                async
                                icon
                                subtitle
                                lazyload
                                getList={this.getSites}
                                value={this.state.site}
                                getOptionSelected={(option, value) => option && value && option.id === value.id}
                                error={this.errors["siteId"] && tried}
                                placeholder={"Select a site"}
                                label={"Site"}
                                onChange={(e, value) => this.updateField("site", value)}
                            />
                        </div>
                        <div className={"row"} style={{marginTop: "15px"}}>
                            <GdCheckbox
                                label={"Automatically create a device for imported data"}
                                checked={this.state.autoCreateAsset}
                                onChange={(e) => this.updateField("autoCreateAsset", e.target.checked)}
                            />
                        </div>
                        {!this.state.autoCreateAsset && !this.state.deviceFromHeader ? (
                            <div className={"row"} style={{marginTop: "15px"}}>
                                <GdAutocomplete
                                    async
                                    icon
                                    subtitle
                                    lazyload
                                    getList={this.getAssets}
                                    value={this.state.asset}
                                    getOptionSelected={(option, value) => option && value && option.id === value.id}
                                    placeholder={"Select a device (optional)"}
                                    label={"Device"}
                                    onChange={(e, value) => this.updateField("asset", value)}
                                />
                            </div>
                        ) : null}
                        {this.state.deviceFromHeader ? (
                            <div className={"row"} style={{marginTop: "15px"}}>
                                <p className={"info-text"}>This import uses a column-per-device layout, so each row routes to the device named in its column header — the single device selection doesn't apply.</p>
                            </div>
                        ) : null}
                    </div>
                ));
            case 3:
                return this.stepWrapper("Format & columns", "Map the CSV columns to import.", (
                    <div>
                        <div className={"row"}>
                            <Input
                                required
                                label={"Format name"}
                                value={this.state.formatName}
                                error={this.errors["formatName"] && tried}
                                onChange={(e) => this.updateField("formatName", e.target.value)}
                            />
                        </div>

                        <p className={"sub-heading"}>Sample CSV (optional)</p>
                        <p className={"info-text"}>Load a sample CSV to pick columns by name and preview sample values. The file is only read in your browser to help configure the import.</p>
                        <div className={"row"}>
                            <input type={"file"} accept={".csv,text/csv"} onChange={this.onCsvSelected}/>
                        </div>
                        <div className={"row"} style={{marginTop: "10px"}}>
                            <GdCheckbox
                                label={"First row is a header"}
                                checked={this.state.hasHeader}
                                onChange={(e) => this.toggleHasHeader(e.target.checked)}
                            />
                        </div>
                        {this.state.csvError ? <p className={"error-text"}>{this.state.csvError}</p> : null}
                        {this.state.csvFileName && !this.state.csvError ?
                            <p className={"info-text"}>{this.state.csvFileName + " — detected " + this.state.columns.length + " column(s)"}</p> : null}

                        <p className={"sub-heading"}>Date column</p>
                        <div className={"value-row"}>
                            {this.renderColumnSelect("Column", this.state.dateIndex,
                                (v) => this.updateField("dateIndex", v), "dateIndex")}
                            <Input
                                required
                                label={"Date format"}
                                placeholder={"e.g. dd-MM-yyyy HH:mm"}
                                value={this.state.dateFormat}
                                error={this.errors["dateFormat"] && tried}
                                onChange={(e) => this.updateField("dateFormat", e.target.value)}
                            />
                        </div>
                        {this.datePreview()}

                        {this.state.deviceFromHeader ? this.renderHeaderLayout(tried) : this.renderStandardLayout(tried)}
                    </div>
                ));
            default:
                return null;
        }
    }

    render() {
        this.checkErrors();
        let editing = !!this.props.item;
        let page = this.state.page;
        let lastPage = 3;
        let footer = (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", flex: "1 1", width: "100%"}}>
                <div>
                    {page > 1 ?
                        <Button label={"Back"} outline color={"gd-grey"} onClick={() => this.setState({page: page - 1})}/> : null}
                </div>
                <div>
                    {page < lastPage ?
                        <Button label={"Next"} disabled={this.blockNext()} onClick={() => this.setState({page: page + 1})}/> :
                        <Button label={editing ? "Save" : "Set Up Import"} progressRes additionalclasses={"sm"} onClick={this.save}/>}
                </div>
            </div>
        );
        return (
            <GdModal title={editing ? "Edit Data Import" : "Set Up Data Import"} open={this.props.open} footer={footer} wide onClose={this.closeModal}>
                <CardBody>
                    <div className={"data-file-import"}>
                        {this.renderStep()}
                    </div>
                </CardBody>
            </GdModal>
        );
    }
}

export default CreateDataFileImport;
